"use client"

import { useState } from 'react'
import { AlertCircle, X, CheckCircle } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/hooks/use-auth'

export default function VerificationBanner() {
  const [isOpen, setIsOpen] = useState(true)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { state } = useAuth()
  const router = useRouter()

  // If user is not authenticated or email is already verified, don't show banner
  if (!state.isAuthenticated || !state.user || state.user.email_verified_at) {
    return null
  }

  const handleResendVerification = async () => {
    setIsVerifying(true)
    setError(null)
    
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/resend-email-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ email: state.user?.email })
      })
      
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || 'Failed to resend verification')
      }
      
      setVerificationSent(true)
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setIsVerifying(false)
    }
  }

  const handleVerifyNow = () => {
    router.push('/verify-email')
  }

  if (!isOpen) return null

  return (
    <div className="bg-yellow-50 border-b border-yellow-200">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              Your email is not verified. Please verify your email to complete your account setup.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {verificationSent ? (
              <div className="flex items-center text-green-600 text-sm">
                <CheckCircle className="w-4 h-4 mr-1" />
                Verification sent!
              </div>
            ) : (
              <>
                <button
                  onClick={handleResendVerification}
                  disabled={isVerifying}
                  className="text-xs font-medium text-yellow-800 hover:text-yellow-900 disabled:opacity-50"
                >
                  {isVerifying ? 'Sending...' : 'Resend verification'}
                </button>
                <button
                  onClick={handleVerifyNow}
                  className="text-xs font-medium px-2 py-1 bg-yellow-100 rounded hover:bg-yellow-200 text-yellow-800"
                >
                  Verify now
                </button>
              </>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="text-yellow-600 hover:text-yellow-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {error && (
          <div className="mt-2 text-xs text-red-600">
            {error}
          </div>
        )}
      </div>
    </div>
  )
} 